import React, { useEffect, useMemo, useState } from 'react';
import { Select } from 'antd';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import {
  conversationTabs,
  registeredConversationTabs,
} from 'constants/conversationTabs';
import { isAdmin, isSmsDashboardUnregisteredRole } from 'utils/userTypeHelper';
import useUser from 'utils/useUser';
import { useSmsDashboardUnreadCount } from 'infrastructure/hooks/smsDashboard/useUnreadConversationsCount';
import { CONVERSATION_TYPES } from 'store/smsDashboard';
import FolderIcon from 'components/atoms/icons/folder-icon';
import ChevronBottom from 'components/atoms/icons/chevron-bottom';
import ChevronTop from 'components/atoms/icons/chevron-top';

import s from './styles.module.scss';

import type { RootDispatch, RootState } from 'store';
import type { SelectProps } from 'antd';

interface Props {
  setSelectMode: (state: boolean) => void;
  setCheckedConversations: React.Dispatch<React.SetStateAction<number[]>>;
  setSearchText: React.Dispatch<React.SetStateAction<string>>;
}

const ConversationTabs: React.FC<Props> = ({
  setSelectMode,
  setCheckedConversations,
  setSearchText,
}) => {
  const { roleNames, userType } = useUser();
  const { t } = useTranslation();

  const {
    smsDashboard: { reset, setConversationType },
  } = useDispatch<RootDispatch>();
  const { conversationType } = useSelector(
    (state: RootState) => state.smsDashboard,
  );
  const [dropdownVisible, setDropdownVisible] = useState(false);
  const [selectedValue, setSelectedValue] = useState<{
    value: string;
    label: React.ReactNode;
  } | null>(null);

  const { unreadConversationsCount, fetchUnreadConversationsCount } =
    useSmsDashboardUnreadCount();

  const hasAccessUnregisteredTab =
    isSmsDashboardUnregisteredRole(roleNames) || isAdmin(userType);
  const usersAllowedConversationTabs = hasAccessUnregisteredTab
    ? conversationTabs
    : registeredConversationTabs;

  const tabNames: { [key: string]: string } = {
    [CONVERSATION_TYPES.RPM]: t('tabs.rpm'),
    [CONVERSATION_TYPES.CCM]: t('tabs.ccm'),
    [CONVERSATION_TYPES.UNREGISTERED_RPM]: t('tabs.unregistered_rpm'),
    [CONVERSATION_TYPES.UNREGISTERED_CCM]: t('tabs.unregistered_ccm'),
  };

  const unreadFields: { [key: string]: string } = {
    [CONVERSATION_TYPES.RPM]: 'rpmTotalUnreadCount',
    [CONVERSATION_TYPES.CCM]: 'ccmTotalUnreadCount',
    [CONVERSATION_TYPES.UNREGISTERED_RPM]: 'unregisteredRpmTotalUnreadCount',
    [CONVERSATION_TYPES.UNREGISTERED_CCM]: 'unregisteredCcmTotalUnreadCount',
  };

  const getUnreadCount = (type: string): number =>
    unreadConversationsCount?.[
      unreadFields[type] as keyof typeof unreadConversationsCount
    ] ?? 0;

  const renderOptionLabel = (type: string): React.ReactNode => (
    <>
      {tabNames[type] || type} <span>({getUnreadCount(type)})</span>
    </>
  );

  const options = useMemo(() => {
    return usersAllowedConversationTabs.map((item) => ({
      value: item,
      label: renderOptionLabel(item),
    }));
  }, [unreadConversationsCount, usersAllowedConversationTabs]);

  useEffect(() => {
    if (unreadConversationsCount !== null) {
      const selected = options.find(
        (option) => option.value === conversationType,
      );
      if (selected) {
        setSelectedValue({
          value: selected.value,
          label: renderOptionLabel(selected.value),
        });
      }
    }
  }, [options, unreadConversationsCount, conversationType]);

  const handleChange: SelectProps['onChange'] = (value) => {
    reset();
    setSearchText('');
    setConversationType(value);
    setSelectMode(false);
    setCheckedConversations([]);
  };

  return (
    <div className={s['sms-dashboard-tabs']}>
      <div className={s['select-wrapper']}>
        <div className={s['prefix-icon-wrapper']}>
          <FolderIcon />
        </div>
        <Select
          id="sms-dashboard-tabs__select"
          className={s['dropdown-style']}
          style={{ width: '100%' }}
          allowClear={false}
          value={selectedValue}
          options={options}
          defaultValue={{
            value: CONVERSATION_TYPES.RPM,
            label: renderOptionLabel(CONVERSATION_TYPES.RPM),
          }}
          onChange={handleChange}
          suffixIcon={dropdownVisible ? <ChevronTop /> : <ChevronBottom />}
          onDropdownVisibleChange={(visible) => {
            setDropdownVisible(visible);
            if (visible) fetchUnreadConversationsCount();
          }}
          dropdownRender={(menu) => <div className={s.dropdown}>{menu}</div>}
          getPopupContainer={(trigger) => trigger.parentNode}
        />
      </div>
    </div>
  );
};

export default ConversationTabs;
